/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, Suspense, useMemo, type ReactNode } from 'react';
import { redirect, useLocation, type RouterContextProvider } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Route } from './+types/_app.$';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { stripPathPrefix } from '@salesforce/storefront-next-runtime/site-context';
import { handlePasswordlessCallback, handlePasswordlessLanding } from '@/lib/auth/passwordless-login.server';
import { handleSocialLoginLanding } from '@/lib/api/auth/social-login.server';
import { handleResetPasswordCallback, handleResetPasswordLanding } from '@/lib/api/auth/reset-password.server';
import { isAbsoluteURL } from '@/lib/utils';
import { getLogger } from '@/lib/logger.server';
import { resolveUrlMapping } from '@/lib/api/url-mapping.server';
import { resolveCategoryIdBySlug } from '@/lib/api/category-slug.server';
import { routeHref, routes } from '@/route-paths';
import { canonicalizeDecoratedPdp } from '@/lib/product/decorated-pdp.server';
import ProductContentSkeleton from '@/components/product-skeleton';
import { Link } from '@/components/link';
import { OptionalLazyErrorBoundary } from '@/components/optional-lazy-error-boundary';
// Reuse the canonical category / product route modules so a legacy URL renders
// *byte-identically* to its `/category/:id` or `/product/:id` twin — same loader,
// same component, just served from the legacy path. See the design note below.
import { loader as categoryLoader, default as CategoryPage } from './_app.category.$categoryId';
import type { loader as productRouteLoader } from './_app.product.$productId';

// RGBARRY-CUSTOMIZATION (perf): legacy category URLs dominate this catch-all. Keep the large PDP module graph
// out of their modulepreload list; load it only for the rare bare URL that resolves to a product.
const loadProductRoute = () => import('./_app.product.$productId');

type ProductLoader = typeof productRouteLoader;

type LoaderHandler = (args: Route.LoaderArgs) => Promise<Response> | Response;
type ActionHandler = (args: Route.ActionArgs) => Promise<Record<string, unknown>>;

// Redirect status codes we honor verbatim from a Business-Manager URL redirect. Anything
// outside this set is treated as a temporary 302 (see the redirect handling in `loader`).
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);

/**
 * Paths that must never trigger a Shopper SEO `getUrlMapping` lookup. Bots, browsers, and
 * scanners hammer this catch-all with `/favicon.ico`, `/.well-known/*`, sourcemap probes,
 * `/wp-login.php`, `/.env`, `/.git/config`, `/backup.sql`, etc.; without this guard every one of
 * those would fire an (uncached) client→MRT→SCAPI round-trip, turning 404 noise into a
 * request-amplification / cost vector. `/robots.txt` has its own resource route, while the guard
 * remains defensive if this loader is called directly.
 *
 * The extension checks are a CLOSED ALLOWLIST — deliberately NOT "any dotted suffix". Legacy
 * SFRA content/category slugs can legitimately contain a dot in their final segment (e.g.
 * `/shipping-returns.html`, `/size-guide-2.0`), and 404ing one of those before resolution would
 * drop a real, indexed URL — the exact SEO loss this feature exists to prevent. Anything not
 * matched here falls through to `getUrlMapping`, which authoritatively decides whether it maps.
 * (Two-segment PDP `.html` URLs are served by the dedicated `_app.$slug.$productId[.html]` route
 * and never reach here; single-segment `.html` content is `_app.$contentId[.html]`.)
 *
 * The four alternations, in order:
 *   1. Well-known dot-directories / dot-files scanners probe for secrets or VCS metadata
 *      (`/.git/config`, `/.env.local`, `/.aws/credentials`, `/.well-known/*`). Matched as a path
 *      PREFIX so the whole subtree is covered regardless of the trailing segment.
 *   2. Ubiquitous CMS/app-scanner path prefixes with no storefront meaning (`/wp-admin/`,
 *      `/xmlrpc`, `/phpmyadmin/`). Extensionless, so the suffix rules below can't catch them.
 *   3. Static-asset extensions (images, fonts, media, sourcemaps, stylesheets, scripts).
 *   4. Server-script / config / secret / archive extensions no legitimate storefront URL uses
 *      (`.php`, `.aspx`, `.jsp`, `.env`, `.sql`, `.bak`, `.zip`, …) — the highest-volume probe
 *      noise, converted to a free 404 instead of a SCAPI round-trip.
 */
const NON_MAPPABLE_PATH = new RegExp(
    [
        '(^\\/\\.(?:well-known|git|svn|hg|bzr|aws|ssh|docker|vscode|idea|env|htaccess|htpasswd|npmrc|ds_store)(?:[\\/.]|$))',
        '(^\\/(?:wp-admin|wp-includes|wp-content|wp-json|xmlrpc|cgi-bin|phpmyadmin)(?:[\\/.]|$))',
        '(\\.(?:ico|txt|xml|json|map|png|jpe?g|gif|svg|webp|avif|bmp|woff2?|ttf|otf|eot|mp4|webm|mov|mp3|pdf|css|js|mjs)$)',
        '(\\.(?:php|phtml|php[0-9]|asp|aspx|jsp|jspa|cgi|pl|py|rb|sh|cfm|env|ini|conf|config|ya?ml|toml|sql|bak|old|swp|log|htaccess|htpasswd|zip|tar|gz|tgz|rar|7z|dll|exe)$)',
    ].join('|'),
    'i'
);

/**
 * RGBARRY-CUSTOMIZATION: byte-identical legacy-URL matching (W-23754187).
 *
 * This is the storefront's single root-level catch-all. It has two jobs:
 *
 *  1. **Configurable auth landing/callback routes** (passwordless, reset-password,
 *     social login) — the SDK positions these on a splat because their paths are
 *     merchant-configurable. Every one of these handlers ends in a `redirect(...)`,
 *     so the surrounding layout is never actually painted for them.
 *
 *  2. **Legacy SFRA URL resolution** — production baggallini.com serves category and
 *     content URLs with no `/:siteId/:localeId` prefix and no `/category/` segment
 *     (e.g. `/crossbody-handbags/`, `/anti-theft/shop-anti-theft/securtex-jet-set/`).
 *     Those slugs come from Business-Manager `pageURL` overrides and can't be derived
 *     from the catalog tree, so we resolve them at request time through the Shopper
 *     SEO `getUrlMapping` endpoint (see `resolveUrlMapping`). CATEGORY / PRODUCT render
 *     *in place* (URL unchanged) to preserve SEO/link equity; a legacy CONTENT_ASSET is
 *     301-redirected to its canonical single-segment `/:contentId.html` route (see
 *     `_app.$contentId[.html]`), which serves content by convention with no further lookup.
 *
 * WHY THIS LIVES UNDER `_app` (not `_empty`): React Router allows exactly one root
 * `*` route. Legacy category/content pages must render inside the standard chrome
 * (header, mega-menu, footer) that the `_app` layout provides, whereas the old
 * `_empty.$` catch-all was chromeless. Since the auth handlers only ever redirect,
 * folding them into the `_app` splat lets a single splat serve both duties. The former
 * `_empty.$` route is retired.
 *
 * ONE TRADE-OFF (accepted): a matched route's PARENT loaders still run in React Router
 * even when the leaf immediately redirects, so auth landing/callback navigations now
 * execute the `_app` layout loader (header / mega-menu / footer fetches) before the
 * redirect resolves — a small added latency and a new failure coupling those chromeless
 * `_empty.$` paths didn't have. Low risk (the handlers redirect on success and the layout
 * data is already warm for real traffic), but it is NOT free; noted here so the cost is
 * visible rather than implied-away.
 *
 * WHY WE REUSE THE ROUTE MODULES: rather than re-implement PLP/PDP data-loading and
 * rendering, we delegate to the canonical `_app.category.$categoryId` /
 * `_app.product.$productId` modules with a synthesized `params` object. This keeps the
 * legacy pages byte-identical to their prefixed twins and free of drift.
 *
 * LATENT COUPLING (not a defect today, but worth knowing): a component deep in the reused
 * category subtree — `components/category-banner` — reads its data via
 * `useRouteLoaderData('routes/_app.category.$categoryId')`, i.e. by ROUTE ID. Under this
 * catch-all the matched route id is `routes/_app.$`, so that hook returns `undefined` here.
 * The banner is null-safe (it renders its fallback, and on this single-site catalog the
 * visible output still matches the native route), so there is no crash and no visible gap
 * TODAY. But manual `params`/prop delegation cannot satisfy a route-id lookup — if a future
 * change makes that banner (or any reused descendant) DEPEND on the hook returning data, the
 * legacy route would silently degrade. The durable fix is to have the framework resolve
 * route-scoped data under the real id rather than hand-wiring it; tracked as a follow-up.
 *
 * KNOWN LIMITATION (documented in docs/README-URL-MATCHING.md): `handle` is static per
 * route, so the catch-all can't switch `handle.ui` per resolved resource type. We adopt
 * the PLP surface (`bg-white`) because categories are the dominant legacy-URL case; a
 * legacy URL that resolves to a PRODUCT renders on that same surface. (CONTENT_ASSET no
 * longer renders here — it redirects to `/:contentId.html`, which owns its own surface.)
 */

/**
 * Extracts pathname from a URI that may be relative or absolute.
 */
function extractPathname(uri: string): string {
    if (isAbsoluteURL(uri)) {
        try {
            return new URL(uri).pathname;
        } catch {
            return uri;
        }
    }
    // MRT environment variable doesn't allow slash, so add it if it's missing
    return uri.startsWith('/') ? uri : `/${uri}`;
}

/**
 * Get the auth loader handler for a given pathname, or null if none matches.
 */
function getLoaderHandler(pathname: string, context: Readonly<RouterContextProvider>): LoaderHandler | null {
    const config = getConfig(context);

    // Gate on `.enabled` (as socialLogin does below): passwordlessLogin's `landingUri` defaults
    // to `/login`, so an unconditional match would route `/login` → handlePasswordlessLanding →
    // redirect(`/login`) → an infinite loop whenever the feature is off. It is off by default
    // (config.server.ts) and only saved today by the dedicated `_app.login` route shadowing
    // `/login`; the `.enabled` guard removes that latent dependency.
    if (
        config.features.passwordlessLogin.enabled &&
        config.features.passwordlessLogin.landingUri &&
        pathname === extractPathname(config.features.passwordlessLogin.landingUri)
    ) {
        return handlePasswordlessLanding;
    }

    // resetPassword is intentionally NOT gated on `.enabled`: unlike passwordlessLogin and
    // socialLogin, the `resetPassword` feature has no `enabled` flag in config.server.ts (it is
    // always available), and its `landingUri` (`/reset-password`) does not default to a value that
    // would self-loop, so the infinite-redirect hazard that motivates the passwordless guard above
    // does not apply here. Matching on `landingUri` alone is correct and mirrors the prior
    // `_empty.$` behavior (no regression).
    if (
        config.features.resetPassword.landingUri &&
        pathname === extractPathname(config.features.resetPassword.landingUri)
    ) {
        return handleResetPasswordLanding;
    }

    if (
        config.features.socialLogin.enabled &&
        config.features.socialLogin.callbackUri &&
        pathname === extractPathname(config.features.socialLogin.callbackUri)
    ) {
        return handleSocialLoginLanding;
    }

    return null;
}

/**
 * Get the auth action handler for a given pathname, or null if none matches.
 */
function getActionHandler(pathname: string, context: Readonly<RouterContextProvider>): ActionHandler | null {
    const config = getConfig(context);

    if (
        config.features.passwordlessLogin.enabled &&
        config.features.passwordlessLogin.callbackUri &&
        pathname === extractPathname(config.features.passwordlessLogin.callbackUri)
    ) {
        return handlePasswordlessCallback;
    }

    if (
        config.features.resetPassword.callbackUri &&
        pathname === extractPathname(config.features.resetPassword.callbackUri)
    ) {
        return handleResetPasswordCallback;
    }

    return null;
}

/**
 * Discriminated loader result. Auth routes and legacy content assets never reach the
 * component (they redirect), so the component only has to render CATEGORY or PRODUCT.
 *
 * The category / product payloads are SPREAD at the top level (not nested under a `data`
 * key) with `kind` as a sibling discriminator. This matters: the delegated loaders return
 * a mix of resolved values (`searchResultCritical`) AND deferred promises
 * (`searchResultNonCritical`, `page`, `categorySchema`, `styleBarData`). The framework only
 * wires those promises for streaming when they sit as TOP-LEVEL keys of the route's
 * loaderData — exactly how the native `/category/:id` route returns them. Nesting them one
 * level deeper (`{ kind, data }`) puts them out of that reach, so the whole payload — even
 * the already-resolved `searchResultCritical` — serializes to `undefined` across the RSC
 * boundary and the reused component crashes reading `.hits`. Keeping the shape identical to
 * the native route is what makes the reused component render byte-identically.
 */
type CatchAllData =
    | ({ kind: 'category' } & Awaited<ReturnType<typeof categoryLoader>>)
    | ({ kind: 'product' } & Awaited<ReturnType<ProductLoader>>);

/**
 * Invoke a sibling route's loader with a synthesized `params` object. The delegated
 * loaders read only `params.categoryId` / `params.productId` (never the splat), so a
 * structural override is safe; the cast bridges the differing generated `params` types.
 */
function delegateLoader<A, T>(routeLoader: (a: A) => T, args: Route.LoaderArgs, params: Record<string, string>): T {
    // Each delegated loader is typed for its own route's `params` (e.g. `{ categoryId }`),
    // which is structurally incompatible with this splat's `{ "*": string }`. We synthesize
    // the shape it reads and cast to that loader's arg type — the runtime object carries
    // everything the loader touches.
    return routeLoader({ ...args, params: { ...args.params, ...params } } as A);
}

export async function loader(args: Route.LoaderArgs): Promise<CatchAllData> {
    const logger = getLogger(args.context);
    const config = getConfig(args.context);
    const url = new URL(args.request.url);
    const strippedPath = stripPathPrefix({ pathname: url.pathname, prefix: config.url?.prefix ?? '' });
    logger.debug('CatchAllRoute: loader starting', { pathname: url.pathname, strippedPath });

    // 1) Configurable auth landing routes (passwordless / reset-password / social).
    //    These always redirect, so the layout below is never rendered for them.
    const handler = getLoaderHandler(strippedPath, args.context);
    if (handler) {
        logger.debug('CatchAllRoute: matched auth loader handler', { pathname: url.pathname });
        return handler(args) as never;
    }

    // 2) Static-asset / well-known noise never maps to a legacy URL — 404 without paying for
    //    a SCAPI round-trip. See NON_MAPPABLE_PATH for why this guard matters for cost/DoS.
    if (NON_MAPPABLE_PATH.test(strippedPath)) {
        logger.debug('CatchAllRoute: non-mappable path, 404 without SEO lookup', {
            pathname: url.pathname,
        });
        throw new Response('Not Found', { status: 404 });
    }

    // 2b) Decorated legacy PDP URL with EXTRA slug segments (W-23754187). Production serves —
    //     and search engines indexed — product URLs like `/lexington-backpack/anything/LEX217.html`.
    //     The dedicated `_app.$slug.$productId[.html]` route only matches the 2-segment form, so a
    //     3+-segment `.html` PDP lands here. Mirror SFRA's platform behavior: resolve the product
    //     by the id in the final segment, derive its canonical slug, and 301 down to the single
    //     `/<slug>/<id>.html`. We do this BEFORE `getUrlMapping` because these decorated URLs won't
    //     resolve there (only the canonical slug is configured), so the lookup would 404 a real,
    //     indexed URL — the exact SEO loss this feature prevents. Logic lives in its own module
    //     (see canonicalizeDecoratedPdp); it returns a ready-to-throw 301, or null to continue.
    const decoratedPdpRedirect = await canonicalizeDecoratedPdp(args.context, strippedPath, url.search);
    if (decoratedPdpRedirect) {
        throw decoratedPdpRedirect;
    }

    // 3) CATEGORY-BY-CONVENTION fast path (W-23754187 / W-23875749). Before paying for the
    //    uncached ~0.5s Shopper SEO `getUrlMapping` round-trip (a blocking waterfall PREFIX in
    //    front of the category loader), try to resolve a legacy category slug directly from the
    //    nav category tree the `_app` layout ALREADY fetches for the mega-menu — a standard,
    //    edge-cacheable response the cartridge hooks enrich with each category's `c_seoUrl`. On a
    //    hit we delegate straight to the category loader with NO `getUrlMapping` call; on a miss we
    //    fall through to the authoritative lookup below (§4), which stays the RARE fallback. This
    //    is exactly Charles's alignment: convention-first, Shopper SEO only when convention can't.
    //
    //    Gate on shape: production categories are extensionless (never `.html`), so we only attempt
    //    this for non-`.html` paths. A bare-segment PRODUCT/CONTENT can't be mis-routed here — its
    //    path won't byte-match a category slug (distinct SFRA namespaces), and a miss simply defers
    //    to §4 which classifies it correctly. `resolveCategoryIdBySlug` never throws.
    if (!/\.html$/i.test(strippedPath)) {
        const conventionCategoryId = await resolveCategoryIdBySlug(args.context, strippedPath);
        if (conventionCategoryId) {
            logger.debug('CatchAllRoute: category resolved by convention (skipped getUrlMapping)', {
                pathname: url.pathname,
                categoryId: conventionCategoryId,
            });
            const data = await delegateLoader(categoryLoader, args, { categoryId: conventionCategoryId });
            // Spread at top level (see CatchAllData) so the loader's deferred promises stream.
            return { kind: 'category', ...data };
        }
    }

    // 4) Legacy SFRA URL resolution via Shopper SEO (RARE fallback — convention above handles the
    //    common category case). The segment is the path without the optional prefix;
    //    `resolveUrlMapping` strips the leading slash / query string.
    const mapping = await resolveUrlMapping(args.context, strippedPath);
    if (!mapping) {
        logger.warn('CatchAllRoute: no URL mapping, returning 404', { pathname: url.pathname });
        throw new Response('Not Found', { status: 404 });
    }

    // 4a) Business-Manager redirect (URL redirect feature, B2C 24.3+). `getUrlMapping`
    //     returns a statusCode + destinationUrl only for a configured redirect. Honor the
    //     merchant's EXACT status so link equity and method semantics transfer correctly:
    //     301 (permanent) and 308 (permanent, method-preserving) pass SEO weight; 302/303/307
    //     stay temporary. Downgrading 308→302 or 307→302 would drop permanence/method intent —
    //     the opposite of this feature's goal. Any unexpected code falls back to a safe 302.
    if (mapping.statusCode && mapping.destinationUrl) {
        const status = REDIRECT_STATUSES.has(mapping.statusCode) ? mapping.statusCode : 302;
        // Normalize the destination. Per the SCAPI `UrlMapping.destinationUrl` schema this is
        // "either a calculated value indicating the Commerce Cloud object URL or your specified
        // URL" and its documented example is relative WITHOUT a leading slash (`mens/clothing/
        // shorts`). Emitting that verbatim in a `Location` header makes the browser resolve it
        // against the *source* path — so a redirect on a nested URL like
        // `/anti-theft/shop-anti-theft/old` would land on
        // `/anti-theft/shop-anti-theft/mens/clothing/shorts` instead of the intended root path,
        // silently defeating the SEO-preservation goal.
        //
        // Classify the destination on SCHEME, NOT on `isAbsoluteURL` (which also returns true for a
        // protocol-relative `//host`). Only a fully scheme-qualified `http(s)://` URL is treated as
        // external and honored verbatim (a legitimate BM cross-domain use case, e.g. moving a
        // retired blog to another host). Everything else must resolve same-origin.
        //
        // To do that safely we collapse ANY leading run of forward-slashes, back-slashes, AND
        // whitespace/control chars down to a single `/`. This defeats the whole family of
        // authority-injection bypasses: `//evil.com`, `///evil.com`, the backslash forms
        // `/\evil.com` / `\evil.com` (the WHATWG URL parser normalizes `\` → `/` for http(s), so a
        // naive `Location: /\evil.com` would resolve off-domain to `https://evil.com`), and the
        // control-char variant `\t/evil.com` (browsers strip leading TAB/LF/CR during URL parsing,
        // re-exposing `//evil.com`). A proper root-relative `/foo` is unaffected (its single leading
        // slash stays one slash); a bare relative segment (`mens/clothing/shorts`) is root-anchored.
        // `destinationUrl` is merchant-authored, but the code's threat model treats it as
        // adversarial, so we normalize uniformly.
        // Strip ALL C0 control chars (incl. CR/LF/TAB) anywhere in the value first. The leading-run
        // collapse below only touches the START, so an INTERIOR `\r`/`\n` would survive into the
        // `Location` header — where Node's `Headers`/`Response` constructor rejects it with
        // `ERR_INVALID_CHAR`, throwing an uncaught `TypeError` (a 500, not a Response) for the whole
        // request. Removing them up front turns a merchant-authored bad value into a still-valid
        // redirect instead of a crash, and closes the CRLF header-injection shape defensively (SCAPI
        // blocks it upstream today, but this resolver's threat model treats `destinationUrl` as
        // adversarial). Applied to BOTH branches so an external target can't smuggle a header split.
        // oxlint-disable-next-line no-control-regex -- deliberately stripping C0 controls + DEL from a Location value
        const sanitizedDestination = mapping.destinationUrl.replace(/[\u0000-\u001f\u007f]/g, '');
        const isExternal = /^https?:\/\//i.test(sanitizedDestination);
        const destination = isExternal ? sanitizedDestination : `/${sanitizedDestination.replace(/^[\s/\\]+/, '')}`;
        // We log external targets for defense-in-depth visibility — an unexpected off-domain
        // redirect authored in BM is worth seeing even though BM is a trusted, privileged surface.
        if (isExternal) {
            logger.warn('CatchAllRoute: BM redirect to an external/absolute URL', {
                from: strippedPath,
                to: destination,
                status,
            });
        }
        logger.debug('CatchAllRoute: BM redirect', {
            from: strippedPath,
            to: destination,
            status,
        });
        throw redirect(destination, status);
    }

    // 4b) Dispatch on the resolved resource type. CATEGORY / PRODUCT render in place (URL
    //     unchanged = byte-identical); CONTENT_ASSET 301-redirects to its `/:contentId.html` route.
    // The SCAPI schema types `resourceType`/`resourceSubType` as UPPERCASE
    // (CATEGORY / PRODUCT / CONTENT_ASSET), but the live Shopper SEO endpoint has been
    // observed returning them lowercase (e.g. `category`) — a runtime/schema contract
    // mismatch. Normalize to uppercase so the dispatch is resilient to either casing.
    const resourceType = mapping.resourceType?.toUpperCase();
    const resourceSubType = mapping.resourceSubType?.toUpperCase();
    switch (resourceType) {
        case 'CATEGORY': {
            if (!mapping.resourceId) break;
            const data = await delegateLoader(categoryLoader, args, { categoryId: mapping.resourceId });
            // Spread at top level (see CatchAllData) so the loader's deferred promises stream.
            return { kind: 'category', ...data };
        }
        case 'PRODUCT': {
            // SFRA PDPs normally arrive via the dedicated `/{slug}/{id}.html` route; a
            // bare segment resolving to a product is rare (e.g. a BM object redirect
            // without .html) but we render it in place for completeness.
            if (!mapping.resourceId) break;
            // RGBARRY-CUSTOMIZATION (perf): resolve the lazily-imported PDP module's loader on demand
            // (the module graph is kept out of the catch-all's modulepreload list; see the const above).
            const { loader: productLoader } = await loadProductRoute();
            const data = await delegateLoader(productLoader, args, { productId: mapping.resourceId });
            return { kind: 'product', ...data };
        }
        case 'CONTENT_ASSET': {
            // Standard content assets live at the dedicated single-segment `/:contentId.html`
            // route (served by convention, no getUrlMapping lookup). A legacy bare content URL
            // that reaches here is redirected there with a permanent 301 so inbound SEO / link
            // equity moves to the canonical `/:contentId.html` URL (`routes.content` ===
            // `/:contentId.html`). Page Designer content assets need the page renderer and are
            // out of scope (tracked in docs/README-URL-MATCHING.md) — fall through to 404.
            if (resourceSubType === 'PAGE_DESIGNER_CONTENT_ASSET' || !mapping.resourceId) break;
            // `routeHref` interpolates `resourceId` into `/:contentId.html` verbatim; this assumes
            // the id carries no `.html` suffix or `/` (a `foo.html` id would yield `/foo.html.html`;
            // an id with `/` would change the path shape into the 2-segment product form). SCAPI
            // content-asset ids are bare slugs, so this holds — noted because the value is treated
            // as adversarial elsewhere in this file.
            throw redirect(routeHref(routes.content, { contentId: mapping.resourceId }), 301);
        }
        default:
            break;
    }

    logger.warn('CatchAllRoute: mapping resolved but not renderable, returning 404', {
        pathname: url.pathname,
        resourceType: mapping.resourceType,
        resourceSubType: mapping.resourceSubType,
    });
    throw new Response('Not Found', { status: 404 });
}

export async function action(args: Route.ActionArgs) {
    const logger = getLogger(args.context);
    const config = getConfig(args.context);
    const url = new URL(args.request.url);
    const strippedPath = stripPathPrefix({ pathname: url.pathname, prefix: config.url?.prefix ?? '' });
    logger.debug('CatchAllRoute: action starting', { pathname: url.pathname, strippedPath });
    const handler = getActionHandler(strippedPath, args.context);

    if (handler) {
        logger.debug('CatchAllRoute: matched action handler', { pathname: url.pathname });
        return handler(args);
    }

    // If no match, throw a 405 Method Not Allowed
    logger.warn('CatchAllRoute: no action handler matched, returning 405', { pathname: url.pathname });
    throw new Response('Method Not Allowed', { status: 405 });
}

// Adopt the PLP surface so a legacy category page matches its `/category/:id` twin.
// See the "KNOWN LIMITATION" note above: `handle` is static, so a PRODUCT resolved via
// this splat shares this same surface.
//
// RGBARRY-CUSTOMIZATION (W-23789028): the GTM page-view classifier (handle.analytics.routeType,
// src/analytics/route-analytics.ts) follows the SAME static-`handle` tradeoff. Bare legacy slugs
// (`/crossbody-handbags`) resolve here in place and are the dominant legacy-URL case, so we label
// this route `'category'`. A rare bare-segment PRODUCT resolved via this splat is mislabeled
// `'category'` for the page view only (PDPs normally use the dedicated `.html` route, which
// re-exports the product handle) — acceptable for the same reason the UI surface is shared, and
// it never affects the product's own `view_item` ecommerce event.
export const handle = {
    ui: { main: { backgroundClass: 'bg-white' } },
    analytics: { routeType: 'category' },
};

/**
 * RGBARRY-CUSTOMIZATION: reuse the product-listing revalidation policy on the catch-all.
 *
 * When a bare legacy slug (e.g. `/crossbody-handbags/`) resolves through here, the MATCHED
 * route is `routes/_app.$` — so React Router applies THIS route's `shouldRevalidate`, not the
 * delegated `_app.category.$categoryId` one. Without an export here, RR defaults to `true`, so
 * every filter/sort/pagination change — and even a client-only filters-panel toggle
 * (`?filters=open`) — would re-run this loader, firing a fresh `getUrlMapping` round-trip
 * (~600ms) PLUS the full category loader on every facet interaction, and bypassing the
 * category route's back-nav cache. On the native `/category/:id` route those are suppressed.
 * (Satisfies the CLAUDE.md rule: export `shouldRevalidate` on routes with URL-driven filtering.)
 *
 * `shouldRevalidate` runs BEFORE the loader, so it can't know whether a given legacy URL will
 * resolve to a CATEGORY or a PRODUCT — it sees only the URLs and form submission. We therefore
 * apply the category (PLP) policy, which is the safe superset for this splat:
 *   - Legacy PLPs (the dominant legacy-URL case) get exactly the right suppression.
 *   - A rare bare-segment PRODUCT degrades gracefully: its param changes (e.g. `?pid=`/swatch)
 *     aren't in the category policy's client-only set and its URL won't be in the PLP back-nav
 *     cache, so the policy falls through to `defaultShouldRevalidate` — identical to the
 *     no-policy behavior it replaces (never LESS revalidation than correctness requires).
 *   - Auth landing/callback + BM-redirect paths always end in a `redirect(...)`, so their
 *     loader result is a thrown Response and revalidation of stale render data is moot.
 * The interim cost: a bare-segment PRODUCT swapping a swatch param may revalidate where the
 * dedicated PDP policy would skip — acceptable, since bare-segment products are rare (PDPs use
 * the dedicated `.html` route) and the outcome is a correct-but-slightly-eager reload, not a bug.
 */
export { shouldRevalidate } from '@/lib/revalidation/routes/category';

// RGBARRY-CUSTOMIZATION (perf): render the rare bare-segment PDP from the lazily-imported product
// module, containing a chunk-load failure to this subtree (OptionalLazyErrorBoundary) and resetting
// the React.lazy cache for a different product path so one transient failure can't poison later
// PDP renders.
function ProductRouteErrorFallback(): ReactNode {
    const location = useLocation();
    const { t } = useTranslation('routeError');
    // Object-form `to` deliberately bypasses the project Link's site-prefix transform. The
    // location already contains any active prefix, so applying it again could duplicate it.
    const retryTo = { pathname: location.pathname, search: location.search, hash: location.hash };

    return (
        <div role="alert" className="min-h-screen bg-background">
            <div className="section-container py-16 text-center">
                <p className="text-base text-muted-foreground">
                    {t('lazyChunkMessage', 'We could not load this page. Please refresh the page.')}
                </p>
                <Link to={retryTo} reloadDocument className="mt-4 inline-block text-primary underline">
                    {t('refreshPage', 'Refresh the page')}
                </Link>
            </div>
        </div>
    );
}

function ProductRouteFallback() {
    return (
        <div className="min-h-screen bg-background" data-testid="catch-all-product-fallback">
            <div className="section-container pt-8 pb-16">
                <ProductContentSkeleton />
            </div>
        </div>
    );
}

function CatchAllProductRoute({ loaderData }: { loaderData: Extract<CatchAllData, { kind: 'product' }> }) {
    const location = useLocation();
    const productPathname = location.pathname;
    // React.lazy memoizes a rejected import. Recreate the wrapper for a different PDP path so a
    // transient chunk failure does not poison later products, while query/hash changes preserve
    // the current product subtree (for example, swatch selection).
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- pathname intentionally resets the lazy cache for a different PDP.
    const ProductPage = useMemo(() => lazy(loadProductRoute), [productPathname]);

    return (
        <OptionalLazyErrorBoundary key={productPathname} fallback={<ProductRouteErrorFallback />}>
            <Suspense fallback={<ProductRouteFallback />}>
                <ProductPage loaderData={loaderData} />
            </Suspense>
        </OptionalLazyErrorBoundary>
    );
}
// END RGBARRY-CUSTOMIZATION

/**
 * Renders the resource resolved by the loader. Auth routes redirect and never reach here.
 */
export default function CatchAllRoute({ loaderData }: { loaderData: CatchAllData }) {
    switch (loaderData.kind) {
        case 'category':
            // `loaderData` is the category payload plus a `kind` discriminator; the extra key
            // is inert (the component destructures only the fields it needs).
            return <CategoryPage loaderData={loaderData} />;
        case 'product':
            // RGBARRY-CUSTOMIZATION (perf): render the bare-segment PDP through the lazily-imported
            // route wrapper (was an eager <ProductPage/>); see CatchAllProductRoute above.
            return <CatchAllProductRoute loaderData={loaderData} />;
        default:
            return null;
    }
}
